import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import "./Header.scss";
import Button from "../Button/Button";
import Modal from "../Modal/Modal";
import LoginForm from "../SignIn/LoginForm";
import RegisterForm from "../Registration/RegisterForm";
import ForgotPasswordForm from "../SignIn/ForgotPasswordForm";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";
import {
  LOGIN_URL,
  REGISTER_URL,
  IS_AUTHENTICATED_URL,
  VERIFY_PHONE_URL,
  RESEND_SMS_URL,
} from "../../features/UrlHelper";
import { Smartphone, ChevronDown, User } from "lucide-react";
import { getAuthToken, setPendingUsername } from "../../features/tenantStorage";
import { useTenant } from "../../context/TenantContext";
import VzhladTlacidlo from "../VzhladPrepinac/VzhladTlacidlo";
import { SESSION_EXPIRED_EVENT, SESSION_EXPIRED_MESSAGE } from "../../features/authEvents";

function Header({ logo, className = "" }) {
  const { isLoggedIn, isAdmin, userRole, userEmail, userPhone, userName, userSurname, login, logout, authLoading } = useAuth();
  const { tenant } = useTenant();
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  // Vysvetlenie nad prihlasovacim formularom (napr. "prihlasenie vyprsalo").
  const [loginInfo, setLoginInfo] = useState("");
  const [registerOpen, setRegisterOpen] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [errorMessageRegistration, setErrorMessageRegistration] = useState("");
  const [errorMessageLogin, setErrorMessageLogin] = useState("");
  const [registrationinng, setRegistrationing] = useState(false);
  const [loggingIn, setLoggingIn] = useState(false);
  // Phone verification modal
  const [phoneVerifyOpen, setPhoneVerifyOpen] = useState(false);
  const [phoneVerifyUserId, setPhoneVerifyUserId] = useState(null);
  const [phoneVerifyPhone, setPhoneVerifyPhone] = useState("");
  const [phoneVerifyCode, setPhoneVerifyCode] = useState("");
  const [phoneVerifyError, setPhoneVerifyError] = useState("");
  const [phoneVerifySubmitting, setPhoneVerifySubmitting] = useState(false);
  const [phoneVerifyResent, setPhoneVerifyResent] = useState(false);

  // widgety pre registraciu, prihlasenie a zabudnute heslo
  const [captchaTokenLogin, setCaptchaTokenLogin] = useState(null);
  const [captchaTokenRegister, setCaptchaTokenRegister] = useState(null);
  const [captchaTokenForgot, setCaptchaTokenForgot] = useState(null);
  const loginTurnstileRef = useRef(null);

  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  const displayName = [userName, userSurname].filter(Boolean).join(" ").trim() || userEmail || userPhone || "Používateľ";
  const workPanelPath =
    userRole === "DRIVER" ? "/vodic"
    : userRole === "ADMIN" || userRole === "SUPERADMIN" || userRole === "MANAGER" ? "/admin"
    : null;

  // Odkazy pre zakaznika (R41). "Objednat" vidi aj NEPRIHLASENY -- menu a kosik
  // sa daju prezerat bez prihlasenia a prihlasenie si vypyta az odoslanie.
  // Prihlasena obsluha ho naopak nevidi: objednavku by aj tak neodoslala
  // (endpoint je za `authCustomer`) a zakaznikov zadava vo svojom paneli.
  // "Moje objednavky" ma zmysel len pre zakaznika.
  const jeObsluha = isLoggedIn && userRole !== "CUSTOMER";
  // `zvyrazneny` -- "Objednať" je to, pre čo človek na stránku prišiel.
  // Ako ďalší sivý odkaz v rade sa stratilo medzi Kontaktom a Prihlásiť sa.
  const links = [
    ...(tenant?.onlineOrdersEnabled && !jeObsluha
      ? [{ label: "Objednať", to: "/objednavka", zvyrazneny: true }] : []),
    ...(userRole === "CUSTOMER" ? [{ label: "Moje objednávky", to: "/moje-objednavky" }] : []),
    { label: "Kontakt", to: "/kontakt" },
  ];

  useEffect(() => {
    setErrorMessageLogin("");
    setErrorMessageRegistration("");
    setErrorMessageLogin("");
    setLoggingIn(false);
    setRegistrationing(false);
  }, [registerOpen, loginOpen]);

  // Prihlasenie vyprsalo (axiosInterceptor alebo casovac v AuthContexte) --
  // AuthContext pouzivatela odhlasil, tu mu rovno ponukneme prihlasenie
  // a povieme preco, aby nehadal, kam zmizol jeho ucet.
  useEffect(() => {
    const onExpired = () => {
      setLoginInfo(SESSION_EXPIRED_MESSAGE);
      setLoginOpen(true);
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, []);

  useEffect(() => {
    if (!loginOpen) setLoginInfo("");
  }, [loginOpen]);

  // rozvozy.sk/<slug>/?prihlasenie -- sem posiela "Prihlasit sa" z marketingovej
  // stranky; prihlasovanie otvorime rovno a parameter z adresy odstranime.
  useEffect(() => {
    if (authLoading || !new URLSearchParams(location.search).has("prihlasenie")) return;
    if (!isLoggedIn) setLoginOpen(true);
    navigate(location.pathname, { replace: true });
  }, [authLoading, isLoggedIn, location.search, location.pathname, navigate]);

  // Close menu on resize to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992) setIsOpen(false);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Close user dropdown on outside click
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setIsUserMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  async function handleLogin(params) {
    if (!captchaTokenLogin) {
      setErrorMessageLogin("Prosím, potvrďte, že nie ste robot.");
      setLoggingIn(false);
      loginTurnstileRef.current?.reset?.();
      setCaptchaTokenLogin(null);
      return;
    }
    try {
      const res = await axios.post(LOGIN_URL, {
        username: params?.username ?? "",
        email: params?.email ?? "",
        password: params?.password ?? "",
        rememberMe: params?.rememberMe === true,
        cfTurnstileToken: captchaTokenLogin,
      });

      setErrorMessageLogin("");
      login(res.data);
      setLoginOpen(false);
      const role = res.data.role;
      if (role === "ADMIN" || role === "SUPERADMIN" || role === "MANAGER") {
        navigate("/admin");
      } else if (role === "DRIVER") {
        navigate("/vodic");
      } else if (role === "CUSTOMER" && tenant?.onlineOrdersEnabled) {
        // Zakaznik sa prihlasuje preto, aby si objednal (R41). Ked prevadzka
        // online objednavky nema, ostava tam, kde bol -- nemal by kam ist.
        navigate("/objednavka");
      }
    } catch (err) {
      if (
        err.response &&
        err.response.status === 400 &&
        err.response.data.type === "phone_verification"
      ) {
        setPhoneVerifyUserId(err.response.data.userId);
        setPhoneVerifyPhone(err.response.data.phone ?? "");
        setPhoneVerifyCode("");
        setPhoneVerifyError("");
        setLoginOpen(false);
        setPhoneVerifyOpen(true);
        return;
      }
      if (
        err.response &&
        err.response.status === 403 &&
        err.response.data.requirePasswordChange
      ) {
        setLoginOpen(false);
        setPendingUsername(params?.username ?? "");
        navigate("/setup-password");
        return;
      }
      setErrorMessageLogin(
        err.response?.data?.message ||
          "Chyba pri prihlásení. Skontrolujte údaje.",
      );
    } finally {
      setLoggingIn(false);
      loginTurnstileRef.current?.reset?.();
      setCaptchaTokenLogin(null);
    }
  }

  async function handleIsLoggedIn() {
    if (!getAuthToken()) {
      return;
    }

    await axios
      .get(IS_AUTHENTICATED_URL)
      .then((res) => {
        console.log("Is authenticated response:", res.data);
      })
      .catch((err) => {
        if (err.response && err.response.status === 401) {
          logout();
        } else {
          console.error("Is authenticated error:", err);
        }
      });
  }

  async function handleRegister(params) {
    if (!captchaTokenRegister) {
      console.error(
        "Registration error:",
        new Error("Captcha token není k dispozici."),
      );
      return;
    }
    await axios
      .post(REGISTER_URL, {
        username: params?.username ?? "",
        email: params?.email ?? "",
        password: params?.password ?? "",
        passwordConfirmation: params?.confirmPassword ?? "",
        name: params?.firstname ?? "",
        surname: params?.lastname ?? "",
        phone_number: (params?.phonePrefix ?? "+421") + (params?.phone ?? ""),
        agree: params?.agree ?? false,
        cfTurnstileToken: captchaTokenRegister,
      })
      .then((res) => {
        setErrorMessageRegistration("");
        setRegistrationing(false);
        setCaptchaTokenRegister(null);
        setRegisterOpen(false);
        setPhoneVerifyUserId(res.data.userId);
        setPhoneVerifyPhone((params?.phonePrefix ?? "+421") + (params?.phone ?? ""));
        setPhoneVerifyCode("");
        setPhoneVerifyError("");
        setPhoneVerifyOpen(true);
      })
      .catch((err) => {
        setRegistrationing(false);
        setErrorMessageRegistration(
          err.response.data.message ||
            "Chyba pri registrácii. Skontrolujte údaje.",
        );
      });
  }


  async function handleVerifyPhone() {
    if (phoneVerifyCode.length !== 8) {
      setPhoneVerifyError("Zadajte platný 8-miestny kód.");
      return;
    }
    setPhoneVerifySubmitting(true);
    setPhoneVerifyError("");
    try {
      const res = await axios.post(VERIFY_PHONE_URL, {
        userId: phoneVerifyUserId,
        code: phoneVerifyCode,
      });
      login(res.data);
      setPhoneVerifyOpen(false);
      setPhoneVerifyCode("");
      setPhoneVerifySubmitting(false);
    } catch (err) {
      setPhoneVerifySubmitting(false);
      setPhoneVerifyError(
        err.response?.data?.message || "Neplatný alebo expirovaný kód.",
      );
    }
  }

  async function handleResendSms() {
    try {
      await axios.post(RESEND_SMS_URL, { userId: phoneVerifyUserId });
      setPhoneVerifyResent(true);
      setTimeout(() => setPhoneVerifyResent(false), 4000);
    } catch (err) {
      setPhoneVerifyError(
        err.response?.data?.message || "Nepodarilo sa odoslať kód.",
      );
    }
  }

  return (
    <>
      <header className={`header ${className}`}>
        <div className="header__container">
          <Link to="/" className="header__logo">
            {logo ? (
              <img src={logo} height="35" alt="Logo" />
            ) : (
              <span className="header__logo-text">Rozvozy</span>
            )}
          </Link>

          <nav className={`header__nav ${isOpen ? "header__nav--open" : ""}`}>
            <div className="public-nav-group">
              {links.map((link, i) => (
                <Link
                  key={i}
                  to={link.to}
                  className={link.zvyrazneny
                    ? "nav-btn-primary"
                    : `nav-btn-ghost ${location.pathname === link.to ? "nav-btn-ghost--active" : ""}`}
                  onClick={() => setIsOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
              {isLoggedIn ? (
                <div className="header__user-menu" ref={userMenuRef}>
                  <button
                    className="header__user-trigger"
                    onClick={() => setIsUserMenuOpen((prev) => !prev)}
                    aria-expanded={isUserMenuOpen}
                    aria-haspopup="true"
                  >
                    <User size={14} className="header__user-icon" />
                    <span className="header__user-name">{displayName}</span>
                    <ChevronDown
                      size={14}
                      className={`header__user-chevron${isUserMenuOpen ? " header__user-chevron--open" : ""}`}
                    />
                  </button>

                  {isUserMenuOpen && (
                    <div className="header__user-dropdown">
                      <button
                        className="header__dropdown-item"
                        onClick={() => {
                          setIsUserMenuOpen(false);
                          setIsOpen(false);
                          navigate("/profil");
                        }}
                      >
                        Profil
                      </button>
                      {workPanelPath && (
                        <button
                          className="header__dropdown-item"
                          onClick={() => {
                            setIsUserMenuOpen(false);
                            setIsOpen(false);
                            navigate(workPanelPath);
                          }}
                        >
                          Pracovný panel
                        </button>
                      )}
                      <div className="header__dropdown-divider" />
                      <button
                        className="header__dropdown-item header__dropdown-item--danger"
                        onClick={() => {
                          setIsUserMenuOpen(false);
                          logout();
                          navigate("/");
                        }}
                      >
                        Odhlásiť sa
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <button
                    className="nav-btn-ghost"
                    onClick={() => setLoginOpen(true)}
                  >
                    Prihlásiť sa
                  </button>
                  {/* Registrácia je samostatná stránka (obsahuje mapu s adresami),
                      nie modál. Ponúka sa len prevádzke, ktorá objednávky cez web
                      naozaj berie — inak by účet nemal na čo slúžiť. */}
                  {tenant?.onlineOrdersEnabled && (
                    <Link
                      to="/registracia"
                      className="nav-btn-ghost"
                      onClick={() => setIsOpen(false)}
                    >
                      Registrovať
                    </Link>
                  )}
                </>
              )}
              {/* Vzhľad pre každého, prihláseného aj nie — na mobile položka
                  bočného panela (burger menu). */}
              <VzhladTlacidlo className="header__vzhlad" />
            </div>
          </nav>

          <button
            className="header__burger"
            onClick={() => setIsOpen(!isOpen)}
            aria-label="Toggle menu"
          >
            <span />
            <span />
            <span />
          </button>

          <div
            className={`header__overlay ${isOpen ? "header__overlay--visible" : ""}`}
            onClick={() => setIsOpen(false)}
          />
        </div>
      </header>

      {/* Login Modal */}
      <Modal
        isOpen={loginOpen}
        onClose={() => setLoginOpen(false)}
        title="Prihlásenie"
      >
        <LoginForm
          onSubmit={(data) => {
            handleLogin(data);
          }}
          // DOČASNE VYPNUTÉ – Registrácia pozastavená
          // onSwitchToRegister={() => { setLoginOpen(false); setRegisterOpen(true); }}
          onForgotPassword={() => {
            setLoginOpen(false);
            setForgotOpen(true);
          }}
          errorMessage={errorMessageLogin}
          infoMessage={loginInfo}
          loggingIn={loggingIn}
          setLoggingIn={setLoggingIn}
          setCaptchaToken={setCaptchaTokenLogin}
          turnstileRef={loginTurnstileRef}
          // „Prihlásiť sa a objednať“ otvára toto okno -- nový zákazník v ňom
          // potrebuje cestu k účtu. Registrácia je len pri online objednávkach.
          registraciaZapnuta={tenant?.onlineOrdersEnabled === true}
          onOdkazNaRegistraciu={() => setLoginOpen(false)}
        />
      </Modal>

      {/* DOČASNE VYPNUTÉ – Registrácia pozastavená
      <Modal
        isOpen={registerOpen}
        onClose={() => setRegisterOpen(false)}
        title="Registrácia"
      >
        <RegisterForm
          onSubmit={(data) => {
            handleRegister(data);
          }}
          onSwitchToLogin={() => {
            setRegisterOpen(false);
            setLoginOpen(true);
          }}
          errorMessage={errorMessageRegistration}
          registrationinng={registrationinng}
          setRegistrationing={setRegistrationing}
          setCaptchaToken={setCaptchaTokenRegister}
        />
      </Modal>
      */}

      {/* Forgot Password Modal */}
      <Modal
        isOpen={forgotOpen}
        onClose={() => setForgotOpen(false)}
        title="Zabudnuté heslo"
      >
        <ForgotPasswordForm
          onBackToLogin={() => {
            setForgotOpen(false);
            setLoginOpen(true);
          }}
          setCaptchaToken={setCaptchaTokenForgot}
        />
      </Modal>

      {/* Phone Verify Modal */}
      <Modal
        isOpen={phoneVerifyOpen}
        onClose={() => {
          setPhoneVerifyOpen(false);
          setPhoneVerifyCode("");
          setPhoneVerifyError("");
        }}
        title="Overenie telefónu"
      >
        <div className="header__phone-verify">
          <div className="header__phone-verify-icon">
            <Smartphone size={36} />
          </div>
          <p className="header__phone-verify-text">
            Zadajte 8-miestny kód zaslaný na číslo{" "}
            <strong>{phoneVerifyPhone}</strong>.
          </p>
          <div className="auth-form__field">
            <label className="auth-form__label" htmlFor="phone-verify-code">
              Overovací kód
            </label>
            <input
              id="phone-verify-code"
              type="text"
              inputMode="numeric"
              maxLength={8}
              className="auth-form__input"
              placeholder="12345678"
              value={phoneVerifyCode}
              onChange={(e) =>
                setPhoneVerifyCode(e.target.value.replace(/\D/g, "").slice(0, 8))
              }
              autoComplete="one-time-code"
            />
          </div>
          {phoneVerifyError && (
            <p className="auth-form__error">{phoneVerifyError}</p>
          )}
          {phoneVerifyResent && (
            <p className="header__phone-verify-resent">
              ✓ Nový kód bol odoslaný.
            </p>
          )}
          <Button
            variant="primary"
            style={{ width: "100%" }}
            onClick={handleVerifyPhone}
            disabled={phoneVerifySubmitting || phoneVerifyCode.length !== 8}
          >
            {phoneVerifySubmitting ? "Overujem..." : "Overiť"}
          </Button>
          <p className="header__phone-verify-resend">
            Neprišiel vám kód?{" "}
            <button
              type="button"
              className="auth-form__link"
              onClick={handleResendSms}
            >
              Zaslať znova
            </button>
          </p>
        </div>
      </Modal>
    </>
  );
}

export default Header;
